/**
 * Admin · Re-query Paystack deposit.
 *
 * Lets an admin paste a Paystack reference and see Paystack's
 * authoritative view of that transaction alongside the local txn,
 * then reconcile if the two are out of sync. The reconciliation
 * itself happens server-side — this page only renders the outcome
 * the backend reports.
 *
 * Discrepancy actions (`is_discrepancy: true`) are styled in danger
 * red so they stand out — these need manual operator review and
 * should not be missed.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatDate, formatKobo } from '../wallet.js';

// Maps a server-side `action` to a friendly label + a colour band for the badge.
// `tone: 'ok'` is green, `'warn'` amber, `'bad'` red — `is_discrepancy` always
// overrides to `'bad'` regardless of action, so we never accidentally hide one.
const ACTION_META = {
  credited: { label: 'Reconciled · credited', tone: 'ok' },
  already_reconciled: { label: 'Already reconciled', tone: 'ok' },
  marked_failed: { label: 'Marked failed', tone: 'warn' },
  already_failed: { label: 'Already final', tone: 'warn' },
  no_change_in_flight: { label: 'Still in flight', tone: 'warn' },
  not_found_at_paystack: { label: 'Not on Paystack', tone: 'warn' },
  no_local_txn: { label: 'No local record', tone: 'bad' },
  discrepancy_amount_mismatch: { label: 'Amount mismatch', tone: 'bad' },
  discrepancy_currency_mismatch: { label: 'Currency mismatch', tone: 'bad' },
  discrepancy_local_terminal_paystack_success: {
    label: 'Local terminal · Paystack success',
    tone: 'bad',
  },
  discrepancy_locally_succeeded_paystack_failed: {
    label: 'Local succeeded · Paystack failed',
    tone: 'bad',
  },
};

export async function renderAdminRequeryDeposit(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">Re-query deposit (admin)</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/admin" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Re-query Paystack</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill bad">ADMIN</span>
    </div>
    <div class="pad" id="rq-page">
      <div class="card" style="padding:12px;">
        <div class="muted" style="font-size:12px;line-height:1.5;">
          Paste a Paystack transaction reference (the <code>trv_dep_…</code> value
          from a deposit row, or whatever the gateway dashboard shows). We re-query
          Paystack's Verify Transaction API and reconcile the local record:
          missed-webhook success → credits the user; failed/abandoned → marks
          local failed; mismatches → flag for manual review.
        </div>
      </div>

      <div class="card" style="padding:12px;margin-top:10px;">
        <form id="rq-form" autocomplete="off">
          <label for="rq-ref" class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;">
            Reference
          </label>
          <div class="row" style="gap:8px;margin-top:6px;">
            <input
              type="text"
              id="rq-ref"
              name="reference"
              placeholder="trv_dep_xxxxxxxxxxxxxxxx"
              style="flex:1;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;"
              required
              maxlength="128"
            />
            <button type="submit" class="btn sm" id="rq-submit">Re-query</button>
          </div>
        </form>
      </div>

      <div id="rq-result"></div>
    </div>
  `;

  const form = document.getElementById('rq-form');
  form.addEventListener('submit', onSubmit);
}

async function onSubmit(e) {
  e.preventDefault();
  const input = document.getElementById('rq-ref');
  const button = document.getElementById('rq-submit');
  const result = document.getElementById('rq-result');
  const reference = (input.value || '').trim();
  if (!reference) return;

  button.disabled = true;
  button.textContent = 'Querying…';
  result.innerHTML = `<div class="card muted" style="padding:12px;margin-top:10px;">Asking Paystack…</div>`;

  try {
    const res = await api('/admin/transactions/verify', {
      method: 'POST',
      body: { reference },
    });
    renderResult(result, res);
    if (res.is_discrepancy) {
      toast.error('Discrepancy flagged — needs manual review.');
    } else if (res.action === 'credited') {
      toast.success('Reconciled and credited.');
    } else if (res.action === 'already_reconciled') {
      toast.success('Already reconciled — no change.');
    } else {
      toast.success('Done.');
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    const code = err.code || '';
    const msg = err.message || 'Re-query failed.';
    result.innerHTML = `
      <div class="card" style="padding:12px;margin-top:10px;color:var(--color-danger);">
        <div style="font-weight:600;margin-bottom:4px;">Could not re-query</div>
        <div style="font-size:13px;">${escape(msg)}</div>
        ${code ? `<div class="muted" style="font-size:11px;margin-top:4px;">code: ${escape(code)}</div>` : ''}
      </div>
    `;
    toast.fromError(err);
  } finally {
    button.disabled = false;
    button.textContent = 'Re-query';
  }
}

function renderResult(host, res) {
  const action = res.action || '';
  const meta = ACTION_META[action] || { label: action || 'Unknown', tone: 'warn' };
  // Discrepancies always render red even if the action label has a softer tone.
  const tone = res.is_discrepancy ? 'bad' : meta.tone;
  const pillClass = tone === 'ok' ? 'status-pill good' : tone === 'bad' ? 'status-pill bad' : 'status-pill';
  // Banner colour shadows the pill so the result is unmistakable at a glance.
  const borderColor =
    tone === 'ok' ? 'var(--color-success,#3ad29f)' :
    tone === 'bad' ? 'var(--color-danger)' :
    'var(--color-warning,#e0a040)';

  host.innerHTML = `
    <div class="card" style="padding:0;margin-top:10px;border-left:4px solid ${borderColor};overflow:hidden;">
      <div style="padding:12px;background:var(--surf-2);">
        <div class="row" style="justify-content:space-between;align-items:center;gap:10px;">
          <div style="font-weight:600;color:#fff;">${escape(meta.label)}</div>
          <span class="${pillClass}">${res.is_discrepancy ? 'DISCREPANCY' : action.toUpperCase()}</span>
        </div>
        <div style="font-size:13px;color:#fff;margin-top:6px;line-height:1.5;">
          ${escape(res.message || '')}
        </div>
      </div>

      ${renderPaystackSection(res.paystack || {})}
      ${renderLocalSection(res.local)}
    </div>
  `;
}

function renderPaystackSection(p) {
  if (p.found === false) {
    return `
      <div style="padding:12px;border-top:1px solid #1a1432;">
        <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">
          Paystack
        </div>
        <div style="font-size:13px;color:#fff;">No transaction with this reference.</div>
      </div>
    `;
  }
  return `
    <div style="padding:12px;border-top:1px solid #1a1432;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">
        Paystack
      </div>
      <div style="display:grid;gap:6px;font-size:13px;">
        ${kvRow('Status', renderPaystackStatus(p.status))}
        ${kvRow('Amount', p.amount_kobo != null ? `${escape(formatKobo(p.amount_kobo))} <span class="muted" style="font-size:11px;">(${p.amount_kobo} kobo)</span>` : '—')}
        ${kvRow('Currency', escape(p.currency || '—'))}
        ${kvRow('Paid at', p.paid_at ? escape(formatDate(p.paid_at)) : '—')}
        ${kvRow('Customer', escape(p.customer_email || '—'))}
        ${kvRow('Gateway response', escape(p.gateway_response || '—'))}
        ${kvRow('Reference', `<span style="font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;">${escape(p.reference || '—')}</span>`)}
      </div>
    </div>
  `;
}

function renderLocalSection(local) {
  if (!local) {
    return `
      <div style="padding:12px;border-top:1px solid #1a1432;">
        <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">
          Local record
        </div>
        <div style="font-size:13px;color:#fff;">None — no Txn matches this reference.</div>
      </div>
    `;
  }
  const changed = local.status_before !== local.status_after;
  return `
    <div style="padding:12px;border-top:1px solid #1a1432;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">
        Local record
      </div>
      <div style="display:grid;gap:6px;font-size:13px;">
        ${kvRow('Txn id', `<span style="font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;">${escape(local.txn_id)}</span>`)}
        ${kvRow('User', escape(local.user_email || local.user_id || '—'))}
        ${kvRow('Amount', `${escape(formatKobo(local.amount_kobo))} <span class="muted" style="font-size:11px;">(${local.amount_kobo} kobo)</span>`)}
        ${kvRow(
          'Status',
          changed
            ? `${renderLocalStatus(local.status_before)} → <b style="color:#fff;">${renderLocalStatus(local.status_after)}</b>`
            : `${renderLocalStatus(local.status_after)} <span class="muted" style="font-size:11px;">(unchanged)</span>`,
        )}
      </div>
    </div>
  `;
}

function kvRow(label, valueHtml) {
  return `
    <div class="row" style="justify-content:space-between;gap:10px;">
      <span class="muted" style="font-size:12px;">${escape(label)}</span>
      <span style="text-align:right;color:#fff;">${valueHtml}</span>
    </div>
  `;
}

function renderPaystackStatus(s) {
  if (!s) return '<span class="muted">—</span>';
  const norm = String(s).toLowerCase();
  if (norm === 'success') return `<span class="status-pill good">${escape(s)}</span>`;
  if (['failed', 'abandoned', 'reversed'].includes(norm)) return `<span class="status-pill bad">${escape(s)}</span>`;
  return `<span class="status-pill">${escape(s)}</span>`;
}

function renderLocalStatus(s) {
  if (!s) return '<span class="muted">—</span>';
  const norm = String(s).toLowerCase();
  if (norm === 'succeeded') return `<span class="status-pill good">${escape(s)}</span>`;
  if (['failed', 'cancelled'].includes(norm)) return `<span class="status-pill bad">${escape(s)}</span>`;
  return `<span class="status-pill">${escape(s)}</span>`;
}
